import { useCallback, useMemo, useState } from 'react';
import { buildSectionConfirmationCopy } from '../../utils/case-plan-section.util';

const EMPTY_COPY = { title: '', description: '', modalStatus: 'danger' };

/**
 * Confirmation state for the destructive section actions (Delete on Draft, Archive on
 * Active/Released). The dialog itself is the shared confirmation `Modal`; this hook only
 * decides what it says and what happens on Confirm.
 *
 * @param {(section: object) => Promise<void> | void} onConfirmed - Runs the mutation for the
 *   confirmed section. Called after the dialog closes so a failure toast is never hidden by it.
 * @returns {{
 *   requestSectionRemoval: (section: object, action: 'delete' | 'archive') => void,
 *   sectionConfirmation: {
 *     isOpen: boolean,
 *     title: string,
 *     description: string,
 *     modalStatus: 'danger' | 'info',
 *     onConfirm: () => void,
 *     onCancel: () => void,
 *   },
 * }}
 */
export const useCasePlanSectionConfirmation = (onConfirmed) => {
  const [pendingRemoval, setPendingRemoval] = useState(null);

  const requestSectionRemoval = useCallback((section, action) => {
    setPendingRemoval({ section, action });
  }, []);

  const cancelSectionRemoval = useCallback(() => {
    setPendingRemoval(null);
  }, []);

  const confirmSectionRemoval = useCallback(() => {
    if (!pendingRemoval) {
      return;
    }

    setPendingRemoval(null);
    onConfirmed(pendingRemoval.section, pendingRemoval.action);
  }, [onConfirmed, pendingRemoval]);

  const copy = useMemo(
    () =>
      pendingRemoval
        ? buildSectionConfirmationCopy(pendingRemoval.section, pendingRemoval.action)
        : null,
    [pendingRemoval],
  );

  return {
    requestSectionRemoval,
    sectionConfirmation: {
      isOpen: copy !== null,
      ...(copy ?? EMPTY_COPY),
      onConfirm: confirmSectionRemoval,
      onCancel: cancelSectionRemoval,
    },
  };
};
